import type { LcComponentStatus, TodayLessonResponse } from '@/api/lcLearning'
import type { StepType, TodayModel, TodayStep, TodayUnitModel } from '../ui-model/types'
import { componentListToStepStatus } from './statusMap'

function detectStepType(beType: string): StepType {
  const t = beType.toLowerCase()
  if (t === 'toon') return 'toon'
  if (t.includes('video')) return 'video'
  if (t.includes('quiz')) return 'quiz'
  if (t === 'guide') return 'guide'
  if (t === 'interactive') return 'interactive'
  return 'other'
}

function buildMeta(comp: LcComponentStatus): string {
  if (comp.type.toLowerCase() === 'warmup') return '생각열기'
  const type = detectStepType(comp.type)
  switch (type) {
    case 'quiz':        return '퀴즈'
    case 'video':       return '영상'
    case 'toon':        return '툰'
    case 'guide':       return '안내'
    case 'interactive': return '인터렉티브 콘텐츠'
    default:            return comp.type
  }
}

function estimateMinutes(comp: LcComponentStatus): number {
  if (comp.type.toLowerCase() === 'warmup') return 2
  const type = detectStepType(comp.type)
  switch (type) {
    case 'quiz':        return 3
    case 'video':       return 4
    case 'toon':        return 2
    case 'guide':       return 1
    case 'interactive': return 3
    default:            return 2
  }
}

export function toTodayUnit(resp: TodayLessonResponse): TodayUnitModel {
  const sorted = [...resp.components].sort((a, b) => a.sortOrder - b.sortOrder)
  const statuses = componentListToStepStatus(sorted)

  const steps: TodayStep[] = sorted.map((comp, i) => ({
    id: comp.configComponentId,
    type: detectStepType(comp.type),
    title: comp.label ?? comp.type,
    meta: buildMeta(comp),
    status: statuses[i],
  }))

  const completedSteps = statuses.filter((s) => s === 'done').length
  const hasStarted = sorted.some((comp) => comp.status === 'in_progress' || comp.status === 'completed')
  const estimatedMinutesLeft = sorted.reduce((acc, comp, i) => {
    if (statuses[i] === 'done') return acc
    return acc + estimateMinutes(comp)
  }, 0)

  return {
    kind: 'lesson',
    enrollmentId: resp.enrollmentId,
    lessonId: resp.lessonId,
    lessonTitle: resp.lessonTitle,
    lessonSequence: resp.lessonSequence ?? null,
    chapterNumber: resp.chapterNumber ?? null,
    moduleTitle: resp.moduleTitle,
    courseTitle: resp.courseTitle,
    isFirstAttempt: resp.isFirstAttempt,
    totalSteps: sorted.length,
    completedSteps,
    hasStarted,
    estimatedMinutesLeft,
    awaitingAdvance: resp.awaitingAdvance ?? false,
    awaitingReviewNoteGrade: resp.awaitingReviewNoteGrade ?? false,
    steps,
    materialNotices: resp.materialNotices ?? [],
  }
}

export function toTodayModel(resp: TodayLessonResponse): TodayModel {
  if (resp.specialLecture) {
    return {
      kind: 'specialLecture',
      lock: resp.specialLecture,
    }
  }
  if (resp.offlineTest) {
    return {
      kind: 'offlineTest',
      lock: resp.offlineTest,
    }
  }
  if (resp.cumulativeAssessment) {
    return {
      kind: 'cumulativeAssessment',
      assessment: resp.cumulativeAssessment,
    }
  }
  return toTodayUnit(resp)
}
